import { httpClient } from '@/core/api/http-client'
import type {
  CreateOutletInput,
  Outlet,
  OutletDetail,
  UpdateOutletInput,
} from '@/domain/entities/outlet.entity'
import type { OutletRepository } from '@/domain/repositories/outlet.repository'
import { unwrap, unwrapList } from '@/infrastructure/shared/unwrap'
import type { OutletDetailDto, OutletDto } from './outlet.dto'
import { outletMapper } from './outlet.mapper'

export class OutletHttpRepository implements OutletRepository {
  async list(): Promise<Outlet[]> {
    const raw = await httpClient.get<unknown>('/outlets', { params: { limit: 100 } })
    return unwrapList<OutletDto>(raw).map(outletMapper.toOutlet)
  }

  async listByTenant(tenantId: string): Promise<Outlet[]> {
    const raw = await httpClient.get<unknown>('/outlets', {
      params: { tenant_id: tenantId, limit: 100 },
    })
    return unwrapList<OutletDto>(raw).map(outletMapper.toOutlet)
  }

  async listCKAndWarehouses(): Promise<Outlet[]> {
    const raw = await httpClient.get<unknown>('/outlets', {
      params: {
        types: 'central_kitchen,warehouse',
        active: true,
        for_procurement: true,
        limit: 100,
      },
    })
    return unwrapList<OutletDto>(raw).map(outletMapper.toOutlet)
  }

  async createOutlet(input: CreateOutletInput): Promise<OutletDetail> {
    const raw = await httpClient.post<unknown>('/outlets', {
      tenant_id: input.tenant_id,
      name: input.name,
      type: input.type ?? 'store',
      address: input.address ?? '',
      timezone: input.timezone ?? 'Asia/Jakarta',
      currency: input.currency ?? 'IDR',
      business_day_cutoff: Number(input.business_day_cutoff) || 0,
    })
    return outletMapper.toOutletDetail(unwrap<OutletDetailDto>(raw))
  }

  async get(id: string): Promise<OutletDetail> {
    const raw = await httpClient.get<unknown>(`/outlets/${id}`)
    return outletMapper.toOutletDetail(unwrap<OutletDetailDto>(raw))
  }

  async update(id: string, input: UpdateOutletInput): Promise<OutletDetail> {
    const isInclusive =
      input.taxType === 'inclusive_after_discount' || input.taxType === 'inclusive_before_discount'

    const raw = await httpClient.put<unknown>(`/outlets/${id}`, {
      name: input.name,
      type: input.type ?? 'store',
      address: input.address ?? '',
      picture: input.picture ?? '',
      tax_type: input.taxType,
      tax_inclusive: isInclusive,
      tax_value: Number(input.taxValue) || 0,
      service_value: Number(input.serviceValue) || 0,
      service_charge_mode: input.serviceChargeMode ?? 'service_charge_is_taxed',
      active: input.active ?? true,
      timezone: input.timezone ?? 'Asia/Jakarta',
      business_day_cutoff: Number(input.businessDayCutoff) || 0,
      kitchen_enabled: input.kitchenEnabled ?? false,
      void_default_prepared: input.voidDefaultPrepared ?? false,
      invoice_prefix: input.invoicePrefix ?? 'INV',
      rounding_enabled: input.roundingEnabled ?? false,
      rounding_unit: Number(input.roundingUnit) || 100,
      rounding_mode: input.roundingMode ?? 'nearest',
      receipt_header: input.receiptHeader ?? '',
      receipt_footer: input.receiptFooter ?? '',
      receipt_show_logo: input.receiptShowLogo ?? true,
      currency: input.currency ?? 'IDR',
      enable_table_qr_static: input.enableTableQRStatic ?? false,
      enable_table_qr_dynamic: input.enableTableQRDynamic ?? false,
    })
    return outletMapper.toOutletDetail(unwrap<OutletDetailDto>(raw))
  }
}
